import React, { PropsWithChildren, useEffect, useState, useRef } from 'react';

import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './TermsAndConditions.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import axios from 'axios';
import SessionStorage from '../../utils/SessionStorage';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import {
    AcquisitionFlow,
    clearSessionAndRedirectToDataEntryPage,
    getAcquisitionFlow,
    getAppData,
} from '../../utils/acquisition-utils';
import withAsyncImport from '../../withAsyncImport';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { InputCheckbox } from 'component-library/index';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { Row, Col, Container } from '../../importCL';
import { isAuthorMode } from '../../utils/cob-utils';
import useAcquisitionProviderFacade from '../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';

const SchumerTableIframe = withAsyncImport(
    () => import('./SchumeTableIframe/SchumeTableIframe').catch((error) => {
        console.error('SchumeTableIframe chunk failed', error);
        clearSessionAndRedirectToDataEntryPage('noTncError');
    }),
);

interface ITermsAndConditionsProps extends MappedComponentProperties {
  bottomBorderAdded?: boolean;
  topBorderAdded?: boolean;
  title?: string;
  subtitle?: string;
  requirementStatusText?: string;
  instructions?: string;
  checkboxAgreeText?: string;
}

export const TermsAndConditionsEditConfig = {
    emptyLabel: 'Terms and Conditions',
    isEmpty: (props: PropsWithChildren<ITermsAndConditionsProps>) => {
        return !props || !props.title;
    },
};

const TermsAndConditions = (
    props: PropsWithChildren<ITermsAndConditionsProps>,
): JSX.Element => {
    const { showPushdown } = usePushdownContext();
    const [termsUrl, setTermsUrl] = useState<string>('');
    const {
        setIsTermsValid,
        isTermsAgreed,
        setIsTermsAgreed,
        isApplicationInfoRecieved,
        isPqOfOneGetComplete,
        isPartnerHostedGetComplete,
    } = useAcquisitionContext();

    const {
        getCurrentMetaAcquisitionFlowState
    } = useAcquisitionProviderFacade();
    const {
        getFeatureFlags
    } = getCurrentMetaAcquisitionFlowState();

    const instructionsRef = useRef<HTMLDivElement>(null);
    const isProduction = window.location.hostname.includes('creditone');
    const authorViewMode = window.location.search.includes('?wcmmode=disabled')
        ? '&wcmmode=disabled'
        : '';

    useEffect(() => {
    //TODO - Receive programcode from a provider when application page components are completed.
        const programCode =
      SessionStorage.get('selectedProgramCode') ||
      PathUtils.getMetaPropertyValue('skipPqProgramCode');
        if (programCode) {
            const servletUrl = `/services/termfinder?programCode=${programCode}`;
            let isTermsUrlUpdated = false;
            axios
                .get(servletUrl)
                .then(response => {
                    if (response.data !== '' && response.data !== undefined) {
                        const url = `${response.data.termsPath}${
                            window.location.port.includes('4502') ? '.html' : ''
                        }?pc=${programCode}${authorViewMode}`;
                        setTermsUrl(url);
                        isTermsUrlUpdated = true;
                        const link = document.createElement('link');
                        link.rel = 'prefetch';
                        link.as = 'document';
                        link.href = url;
                        document.head.appendChild(link);
                    }
                })
                .catch(err => {
                    console.error(err);
                })
                .finally(() => {
                    if (!isTermsUrlUpdated && isProduction) {
                        clearSessionAndRedirectToDataEntryPage('noTncError');
                    }
                });
        } else if (
            isProduction &&
      (isApplicationInfoRecieved ||
        isPartnerHostedGetComplete ||
        isPqOfOneGetComplete)
        ) {
            clearSessionAndRedirectToDataEntryPage('noTncError');
        }
    }, [
        isApplicationInfoRecieved,
        isPqOfOneGetComplete,
        isPartnerHostedGetComplete,
    ]);

    function termsHandler(event: MessageEvent) {
        if (event.type === 'message' && event.data === 'terms_invalid') {
            setIsTermsValid(false);

            if (getAcquisitionFlow() === AcquisitionFlow.SkipPQ) {
                showPushdown('noTncError');
            } else {
                clearSessionAndRedirectToDataEntryPage('noTncError');
            }
            return;
        }
    }

    useEffect(() => {
        if (instructionsRef.current) {
            const targetEl =
        instructionsRef.current.querySelector('.disclosure-link');
            targetEl?.setAttribute('href', termsUrl);
        }
        window.addEventListener('message', termsHandler);
        return () => {
            window.removeEventListener('message', termsHandler);
        };
    }, [termsUrl]);

    useEffect(() => {
        if (isAuthorMode()) {
            setIsTermsValid(true);
            const loader = document.getElementById('Loader__wrapper');
            const spaRoot = document.getElementById('spa-root');
            if (spaRoot && loader) {
                loader.classList.add('no-display');
                spaRoot.removeAttribute('style');
            }
        }
    }, []);

    useEffect(() => {
        if (getFeatureFlags('DPM-12192-ApplicationCtaValidationsRefactor')) {
            SessionStorage
                .tryGetCipher<Record<string, unknown>>('persistingFlowApplicationData', {})
                .then((persistedData: Record<string, unknown>) => {
                    setIsTermsAgreed(!!persistedData?.AgreedToPrivacyPolicy);
                })
        } else {
            getAppData().then(appData => {
                // always false for PHA since there is no corresponding flag in new api.
                setIsTermsAgreed(!!appData?.AgreedToPrivacyPolicy);
            });
        }
    }, []);

    return (
        <Container
            id='terms-and-conditions'
            className='TermsAndConditions no-print'
        >
            {props.topBorderAdded && 
                <div className='md:px-4'>
                    <hr className='TermsAndConditions__hr' />    
                </div>
            }
            <Row className='TermsAndConditions__rowContainer'>
                <Col className='TermsAndConditions__titleContainer' xs={12}>
                    {props.title && (
                        <h3 className='TermsAndConditions__title mb-6 px-4 md:px-8'>
                            {transformHTMLParserLinks(props.title)}
                        </h3>
                    )}
                    {props.subtitle && (
                        <p className='TermsAndConditions__subtitle px-5 md:px-8'>
                            {transformHTMLParserLinks(props.subtitle)}
                        </p>
                    )}
                    {props.requirementStatusText && (
                        <i className='TermsAndConditions__requirementStatusText'>
                            {transformHTMLParserLinks(props.requirementStatusText)}
                        </i>
                    )}
                </Col>
                <Col className='TermsAndConditions__SchumerTableContainer' xs={12}>
                    {!isAuthorMode() && (
                        <SchumerTableIframe
                            termsUrl={termsUrl}
                            reload={true}
                            onLoadStateChange={state => {
                                if (state === 'ready') {
                                    setIsTermsValid(true);
                                    const loader = document.getElementById('Loader__wrapper');
                                    const spaRoot = document.getElementById('spa-root');
                                    if (spaRoot && loader) {
                                        loader.classList.add('no-display');
                                        spaRoot.removeAttribute('style');
                                    }

                                }
                                if (state === 'failed') {
                                    setIsTermsValid(false);
                                }
                            }}
                        />
                    )}
                </Col>
                {props.instructions && (
                    <div
                        ref={instructionsRef}
                        className='TermsAndConditions__instructions mt-2 mb-2 px-8 md:px-12'
                    >
                        {transformHTMLParserLinks(props.instructions)}
                    </div>
                )}
                {props.checkboxAgreeText && (
                    <Col className='TermsAndConditions__agreeText no-print md:px-12' xs={12}>
                        <form name='agreeTermsAndConditions'>
                            <InputCheckbox
                                id='agreeTermsAndConditionsId'
                                name='agreeTermsAndConditions'
                                label={transformHTMLParserLinks(props.checkboxAgreeText)}
                                value={isTermsAgreed}
                                setValue={setIsTermsAgreed}
                                labelClass='TermsAndConditions__agreeLabel'
                                checkboxId='agree_t_c_c'
                            />
                        </form>
                    </Col>
                )}
            </Row>
            {props.bottomBorderAdded && <hr className='TermsAndConditions__hr' />}
        </Container>
    );
};

export default TermsAndConditions;